import { Schema } from 'effect';

import { AuthorisationServerRealm, EmailAddress } from '@finnest/core/effect';

import { HttpUrl } from '../_shared/formats.ts';
import { regulatoryEcosystemSchema } from '../opin/source/source-mode.ts';
import type { RegulatoryEcosystem } from '../opin/source/source-mode.ts';

import { participantDirectorySyncCounterFields } from './_shared/sync-counters.ts';
import { smokeCheckItemSchema } from './_shared/smoke-check.ts';
import { jsonObjectSchema } from './vocabulary/json-value.ts';
import {
  ClientId,
  CustomerId,
  OrganisationId,
  OrganizationId,
  ParticipantId,
  SoftwareId,
} from './vocabulary/ids/branded-ids.ts';

export const onboardingRegulatoryEnvironmentSchema = Schema.Literals(['sandbox', 'production']);
export type OnboardingRegulatoryEnvironment = typeof onboardingRegulatoryEnvironmentSchema.Type;

export { regulatoryEcosystemSchema as onboardingRegulatoryEcosystemSchema };
export type OnboardingRegulatoryEcosystem = RegulatoryEcosystem;

export const onboardingCertificateKindSchema = Schema.Literals(['a1', 'a3']);
export type OnboardingCertificateKind = typeof onboardingCertificateKindSchema.Type;

export const onboardingCertUploadInputSchema = Schema.Struct({
  customerId: CustomerId,
  certificateKind: onboardingCertificateKindSchema,
  transport: Schema.NonEmptyString,
  signing: Schema.NonEmptyString,
}).annotate({
  title: 'Power onboarding certificate upload input',
  description: 'Internal Power onboarding payload for validating P1 ICP-Brasil certificates.',
});
export type OnboardingCertUploadInput = typeof onboardingCertUploadInputSchema.Type;

export const onboardingCertUploadResponseSchema = Schema.Struct({
  transportCertOk: Schema.Boolean,
  signingCertOk: Schema.Boolean,
  icpChainValidationOk: Schema.Boolean,
}).annotate({
  title: 'Power onboarding certificate upload response',
  description: 'Internal Power onboarding response for P1 ICP-Brasil certificate validation.',
});
export type OnboardingCertUploadResponse = typeof onboardingCertUploadResponseSchema.Type;

export const onboardingPs256GenerateInputSchema = Schema.Struct({
  customerId: CustomerId,
  kid: Schema.optionalKey(Schema.NonEmptyString),
}).annotate({
  title: 'Power onboarding PS256 key generation input',
  description: 'Internal Power onboarding payload for generating a P1 PS256 key.',
});
export type OnboardingPs256GenerateInput = typeof onboardingPs256GenerateInputSchema.Type;

export const onboardingPs256GenerateResponseSchema = Schema.Struct({
  kid: Schema.NonEmptyString,
  jwksUrl: HttpUrl,
}).annotate({
  title: 'Power onboarding PS256 key generation response',
  description: 'Internal Power onboarding response for P1 PS256 key generation.',
});
export type OnboardingPs256GenerateResponse = typeof onboardingPs256GenerateResponseSchema.Type;

export const onboardingSsaFetchInputSchema = Schema.Struct({
  customerId: CustomerId,
  // The SSA is fetched from the per-ecosystem directory (OFB vs OPIN), so the
  // ecosystem selects which directory host + persisted registration to use.
  ecosystem: regulatoryEcosystemSchema,
  env: onboardingRegulatoryEnvironmentSchema,
}).annotate({
  title: 'Power onboarding SSA fetch input',
  description: 'Internal Power onboarding payload for fetching a software statement assertion.',
});
export type OnboardingSsaFetchInput = typeof onboardingSsaFetchInputSchema.Type;

export const onboardingSsaFetchResponseSchema = Schema.Struct({
  issuer: Schema.NonEmptyString,
  softwareId: SoftwareId,
  organizationId: OrganizationId,
  claims: jsonObjectSchema,
}).annotate({
  title: 'Power onboarding SSA fetch response',
  description: 'Internal Power onboarding response for decoded SSA claims.',
});
export type OnboardingSsaFetchResponse = typeof onboardingSsaFetchResponseSchema.Type;

// Per-ecosystem participant-directory registration the operator captures so the
// SSA fetch can authenticate to the real directory: the registered
// organisation / software statement ids form the assertion URL path, and the
// directory client id is used for the `client_credentials` + `private_key_jwt`
// token request. `signingKid` selects which P1.2 PS256 key signs the client
// assertion; when omitted the sole generated key is used. `redirectUris` are the
// registered client redirect URIs — they are not advertised in the AS discovery
// document, so the operator captures them here for the P1.7 AS-metadata view and
// the P1.8 DCR registration payload.
export const onboardingSetDirectoryRegistrationInputSchema = Schema.Struct({
  customerId: CustomerId,
  ecosystem: regulatoryEcosystemSchema,
  organisationId: OrganisationId,
  softwareId: SoftwareId,
  clientId: ClientId,
  signingKid: Schema.optionalKey(Schema.NonEmptyString),
  redirectUris: Schema.optionalKey(Schema.Array(HttpUrl).check(Schema.isMinLength(1))),
}).annotate({
  title: 'Power onboarding directory registration input',
  description:
    'Internal Power onboarding payload capturing the per-ecosystem directory organisation/software/client ids used by the P1 SSA fetch.',
});
export type OnboardingSetDirectoryRegistrationInput =
  typeof onboardingSetDirectoryRegistrationInputSchema.Type;

export const onboardingSetDirectoryRegistrationResponseSchema = Schema.Struct({
  ecosystem: regulatoryEcosystemSchema,
  organisationId: OrganisationId,
  softwareId: SoftwareId,
  clientId: ClientId,
}).annotate({
  title: 'Power onboarding directory registration response',
  description:
    'Internal Power onboarding response echoing the persisted per-ecosystem directory registration.',
});
export type OnboardingSetDirectoryRegistrationResponse =
  typeof onboardingSetDirectoryRegistrationResponseSchema.Type;

export const onboardingDirectorySyncInputSchema = Schema.Struct({
  customerId: CustomerId,
  ecosystem: regulatoryEcosystemSchema,
  env: onboardingRegulatoryEnvironmentSchema,
}).annotate({
  title: 'Power onboarding participant directory sync input',
  description: 'Internal Power onboarding payload for triggering participant directory sync.',
});
export type OnboardingDirectorySyncInput = typeof onboardingDirectorySyncInputSchema.Type;

export const onboardingDirectorySyncResponseSchema = Schema.Struct({
  ...participantDirectorySyncCounterFields,
}).annotate({
  title: 'Power onboarding participant directory sync response',
  description: 'Internal Power onboarding response with participant directory sync counts.',
});
export type OnboardingDirectorySyncResponse = typeof onboardingDirectorySyncResponseSchema.Type;

// The regulatory Keycloak realm names are an axis of the shared ecosystem policy
// (`authorisationServerRealm`); this contract names the same vocabulary rather than redeclaring it.
export const onboardingKcRealmSchema = AuthorisationServerRealm;

export const onboardingKcValidateRealmInputSchema = Schema.Struct({
  customerId: CustomerId,
  realm: onboardingKcRealmSchema,
}).annotate({
  title: 'Power onboarding Keycloak realm validation input',
  description: 'Internal Power onboarding payload for validating regulatory Keycloak realms.',
});
export type OnboardingKcValidateRealmInput = typeof onboardingKcValidateRealmInputSchema.Type;

export const onboardingKcValidateRealmResponseSchema = Schema.Struct({
  realm: onboardingKcRealmSchema,
  validationOk: Schema.Boolean,
  driftCount: Schema.Int.check(Schema.isGreaterThanOrEqualTo(0)),
}).annotate({
  title: 'Power onboarding Keycloak realm validation response',
  description: 'Internal Power onboarding response for regulatory Keycloak realm validation.',
});
export type OnboardingKcValidateRealmResponse = typeof onboardingKcValidateRealmResponseSchema.Type;

export const onboardingP1SmokeInputSchema = Schema.Struct({
  customerId: CustomerId,
  ecosystem: regulatoryEcosystemSchema,
}).annotate({
  title: 'Power onboarding P1 smoke input',
  description: 'Internal Power onboarding payload for the P1 smoke suite.',
});
export type OnboardingP1SmokeInput = typeof onboardingP1SmokeInputSchema.Type;

export const onboardingP1SmokeResponseSchema = Schema.Struct({
  passed: Schema.Boolean,
  checks: Schema.Array(smokeCheckItemSchema),
}).annotate({
  title: 'Power onboarding P1 smoke response',
  description: 'Internal Power onboarding response for the P1 smoke suite.',
});
export type OnboardingP1SmokeResponse = typeof onboardingP1SmokeResponseSchema.Type;

const counterpartyContactSchema = Schema.Struct({
  id: Schema.NonEmptyString,
  ecosystem: regulatoryEcosystemSchema,
  participantId: ParticipantId,
  contactName: Schema.NonEmptyString,
  role: Schema.NonEmptyString,
  email: Schema.optionalKey(EmailAddress),
  phone: Schema.optionalKey(Schema.NonEmptyString),
  notes: Schema.optionalKey(Schema.NonEmptyString),
});

export const onboardingCounterpartyContactsInputSchema = Schema.Struct({
  customerId: CustomerId,
  contacts: Schema.Array(counterpartyContactSchema).check(Schema.isMinLength(1)),
}).annotate({
  title: 'Power onboarding counterparty contacts input',
  description: 'Internal Power onboarding payload for P5 counterparty contact planning.',
});
export type OnboardingCounterpartyContactsInput =
  typeof onboardingCounterpartyContactsInputSchema.Type;

export const onboardingCounterpartyContactsResponseSchema = Schema.Struct({
  upserted: Schema.Int.check(Schema.isGreaterThanOrEqualTo(0)),
}).annotate({
  title: 'Power onboarding counterparty contacts response',
  description: 'Internal Power onboarding response for P5 counterparty contact planning.',
});
export type OnboardingCounterpartyContactsResponse =
  typeof onboardingCounterpartyContactsResponseSchema.Type;
